/*
* ---------------------------------------------------------------------------
* COMMERCIAL IN CONFIDENCE
*
* (c) Copyright Quosient Ltd. All Rights Reserved.
*
* See LICENSE.txt in the repository root.
* ---------------------------------------------------------------------------
*/
import Vue from 'vue';
import VueRouter from 'vue-router';
import Home from '../views/Home.vue';
import Suggestion from '../views/Suggestion.vue';
import Tutorials from '../views/Tutorials.vue';
import SignIn from '../components/SignIn.vue';
import StaticSignIn from '../components/StaticSignIn.vue';
import Dash from '../components/OrgDashboard.vue';
import Workflow from '../views/Workflow.vue';
import MyAccount from '../views/MyAccount.vue';
import Admin from '../views/Admin.vue';
import UserList from '../components/UserList.vue';
import OrganisationList from '../components/OrganisationList.vue';
import OrganisationFeatures from '../components/OrganisationFeatures.vue';
// import Tests from '../components/Tests.vue';
import ResetPassword from '../components/ResetPassword.vue';
import { AuthService } from '../services/auth.service';

Vue.use(VueRouter);

// UNEP dashboard imports and constants.

import Dashboard from "../views/OADashboard.vue"
const USE_OPEN_ACCESS_DASHBOARD = process.env.VUE_APP_USE_OPEN_ACCESS_DASHBOARD == 'true';

// Default Earth Blox Builder Routes.
const defaultRoutes = [
    {
        path: '/lti/launch'

    },
    {
        path: '/staticsignin',
        name: 'StaticSignIn',
        component: StaticSignIn,
        meta: {
            title: 'Earth Blox | Static Sign In',
        }
    },
    {
        path: '/signin',
        name: 'SignIn',
        component: SignIn,
        meta: {
            title: 'Earth Blox | Sign In',
        }
    },
    {
        path: '/reset',
        name: 'ResetPassword',
        component: ResetPassword,
        meta: {
            title: 'Earth Blox | Reset Password',
        }
    },
    {
        path: '/',
        name: 'Home',
        component: Home,
        meta: {
            title: 'Earth Blox | Home',
        }
    },
    {
        path: '/suggestion',
        name: 'Suggestion',
        component: Suggestion,
        meta: {
            title: 'Earth Blox | Make a suggestion',
        }
    },
    {
        path: '/tutorials',
        name: 'Tutorials',
        component: Tutorials,
        meta: {
            title: 'Earth Blox | Tutorials',
        }
    },
    {
        path: '/dash/:orgId',
        name: 'dash',
        component: Dash,
        props: true,
        meta: {
            requiresAuth: true,
            title: 'Earth Blox | Organisation Dashboard',
        }
    },
    {
        path: '/workflow/:workflowId?',
        name: 'Workflow',
        component: Workflow,
        props: true,
        meta: {
            requiresAuth: true,
            title: 'Earth Blox | Workspace',
        }
    },
    {
        path: '/myaccount',
        name: 'MyAccount',
        component: MyAccount,
        meta: {
            requiresAuth: true,
            title: 'Earth Blox | My Account',
        }
    },
    {
        path: '/admin',
        name: 'Admin',
        component: Admin,
        meta: {
            requiresAuth: true,
            title: 'Earth Blox | Admin Panel',
        },
        children: [
            {
                name: 'Admin-Users',
                path: 'users',
                component: UserList,
                meta: {
                    title: 'Earth Blox | Users',
                },
            },
            {
                name: 'Admin-Features',
                path: 'features',
                component: OrganisationFeatures,
                meta: {
                    title: 'Earth Blox | Features',
                },
            },
            {
                name: 'Admin-Organisations',
                path: 'organisations',
                component: OrganisationList,
                meta: {
                    title: 'Earth Blox | Organisations',
                },
            }
        ]
    }
]

const defaultRouter = new VueRouter({
    // TODO Consider history mode, so we don't get hashes in the URL!
    // mode: 'history',
    // See caveats and guidance at https://medium.com/swlh/using-vue-routers-history-mode-6d9395e8122e
    // and https://router.vuejs.org/guide/essentials/history-mode.html#hash-mode
    routes: defaultRoutes
});

// Check for login requirement on every page
defaultRouter.beforeEach((to, from, next) => {
    // console.log(`defaultRouter.beforeEach currentRoute ${router.currentRoute.name} ${router.currentRoute.path} FROM ${from.name} ${from.path} TO ${to.name} ${to.path}`)
    if (to.matched.some(record => record.meta.requiresAuth)) {
        console.log(`route requires auth`)
        
        // this route requires auth, check if logged in
        // if not, redirect to login page.
        if (AuthService.isUserLoggedIn()) {
            console.info("user logged in already ");
            next();
        }
        else {

            if (to.query.firebaseToken) {
                AuthService.signInWithCustomToken(to.query.firebaseToken)
                    .then((userCredential) => {
                        // Signed in
                        console.log("signed in with custom token user " + userCredential.user.uid);
                        next();
                    })
                    .catch((error) => {
                        var errorCode = error.code;
                        var errorMessage = error.message;
                        console.log("Error signing in with custom token:" + errorMessage + "code:" + errorCode);
                        next({
                            path: '/',
                        });
                    });
            }

            if (to.query.ltiToken) {
                let workflowid = to.query.workflowid ? to.query.workflowid : '0';

                AuthService.signInWithCustomToken(to.query.ltiToken)
                    .then((userCredential) => {
                        // Signed in
                        console.log("signed in with custom token user " + userCredential.user.uid);
                        next({
                            path: '/workflow/' + workflowid

                        });
                    })
                    .catch((error) => {
                        var errorCode = error.code;
                        var errorMessage = error.message;
                        console.log("Error signing in with custom token:" + errorMessage + "code:" + errorCode);
                        next({
                            path: '/',
                        });
                    });
            }
            next({
                path: '/',
            });
        }
    }
    else {
        console.log(`route does not require auth`)

        // Login is not required; but if this is the home page or signin page and the user is logged in, redirect them to the workspace!
        if (AuthService.isUserLoggedIn() && to.name == "Home") {
            next({
                path: "/workflow/",
                replace: true,
            });
        }
        next(); // make sure to always call next()!
    }

    if (to.matched.some(record => record.meta.title)) {
        const pageWithTitle = to.meta.title;
        if (pageWithTitle) {
            document.title = pageWithTitle;
        } else {
            document.title = 'Earth Blox';
        }

        next();

    } else {
        document.title = 'Earth Blox | Page Not Found';
        next(); // make sure to always call next()!
    }

});

/**
 * Open Access Dashboard Route Config and Router
 */

const dashboardRoutes = [
    {
        path: '/',
        name: 'Dashboard',
        component: Dashboard
    },
    {
        path: '/signin',
        name: 'SignIn',
        component: SignIn,
        meta: {
            title: 'Strata | Sign In',
        }
    }
]

const dashboardRouter = new VueRouter({
    mode: 'history',
    routes: dashboardRoutes
});

// switch routers if using the Open Access Dashboard.
const router = USE_OPEN_ACCESS_DASHBOARD ? dashboardRouter : defaultRouter;

export default router
